import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguageSwitcher } from "@/hooks/useLanguageSwitcher";
import { SUPPORTED_SEO_LANGS, SeoLang } from "@/lib/seo-config";

type Variant = "compact" | "full";

const META: Record<SeoLang, { flag: string; code: string; name: string }> = {
  da: { flag: "🇩🇰", code: "DA", name: "Dansk" },
  en: { flag: "🇬🇧", code: "EN", name: "English" },
  nb: { flag: "🇳🇴", code: "NO", name: "Norsk" },
  sv: { flag: "🇸🇪", code: "SV", name: "Svenska" },
  de: { flag: "🇩🇪", code: "DE", name: "Deutsch" },
  fr: { flag: "🇫🇷", code: "FR", name: "Français" },
  es: { flag: "🇪🇸", code: "ES", name: "Español" },
  pt: { flag: "🇵🇹", code: "PT", name: "Português" },
};

interface Props {
  variant?: Variant;
  className?: string;
}

export function LanguageSwitcher({ variant = "compact", className }: Props) {
  const { i18n, t } = useTranslation("ui");
  const changeLang = useLanguageSwitcher();
  const current = (SUPPORTED_SEO_LANGS as string[]).includes(i18n.language)
    ? (i18n.language as SeoLang)
    : "en";

  const isCompact = variant === "compact";

  return (
    <Select value={current} onValueChange={(v) => changeLang(v)}>
      <SelectTrigger
        className={
          isCompact
            ? `h-9 w-auto gap-1.5 rounded-full border-border/60 px-3 text-sm ${className ?? ""}`
            : `h-10 w-[180px] ${className ?? ""}`
        }
        aria-label={t("languageSwitcher.selectLanguage")}
      >
        <SelectValue>
          {isCompact ? (
            <span className="flex items-center gap-1.5">
              <span aria-hidden>{META[current].flag}</span>
              <span className="font-medium">{META[current].code}</span>
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <span aria-hidden>{META[current].flag}</span>
              <span>{META[current].name}</span>
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent align="end" className="min-w-[180px]">
        {SUPPORTED_SEO_LANGS.map((lang) => (
          <SelectItem key={lang} value={lang}>
            <span className="flex items-center gap-2">
              <span aria-hidden>{META[lang].flag}</span>
              <span>{META[lang].name}</span>
              <span className="ml-auto text-xs text-muted-foreground">{META[lang].code}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
